import { CurrencyCodeType, PaymentRailType } from '@/types/currency';
import { GenericAction } from '@/utility/reducers';

export type DestinationType = 'caliza_wallet' | 'external';

export interface EarningsWithdrawalFlowState {
  readonly destinationType: DestinationType;
  readonly fromAmount: number | null;
  readonly fromCurrency: CurrencyCodeType;
  readonly targetCurrency: CurrencyCodeType;
  readonly paymentRail: PaymentRailType;
  readonly recipientId: string | null;
  readonly onlyMoveToCustody: boolean;
}

export const initialState: EarningsWithdrawalFlowState = {
  destinationType: 'caliza_wallet',
  fromAmount: null,
  fromCurrency: CurrencyCodeType.USD,
  targetCurrency: CurrencyCodeType.USD,
  paymentRail: PaymentRailType.CalizaWallet,
  recipientId: '',
  onlyMoveToCustody: true,
};

export enum EarningsWithdrawalActionType {
  destinationTypeChanged = 'destinationTypeChanged',
  recipientChanged = 'recipientChanged',
  amountChanged = 'amountChanged',
  targetCurrencyChanged = 'targetCurrencyChanged',
  reset = 'reset',
}

export type DestinationTypeChangedAction = GenericAction<
  EarningsWithdrawalActionType.destinationTypeChanged,
  DestinationType
>;

export type RecipientChangedAction = GenericAction<
  EarningsWithdrawalActionType.recipientChanged,
  { recipientId: string; paymentRail: PaymentRailType; currency: CurrencyCodeType }
>;

export type AmountChangedAction = GenericAction<
  EarningsWithdrawalActionType.amountChanged,
  number | null
>;

export type TargetCurrencyChangedAction = GenericAction<
  EarningsWithdrawalActionType.targetCurrencyChanged,
  CurrencyCodeType
>;

export type ResetAction = GenericAction<EarningsWithdrawalActionType.reset, void>;

export type EarningsWithdrawalAction =
  | DestinationTypeChangedAction
  | RecipientChangedAction
  | AmountChangedAction
  | TargetCurrencyChangedAction
  | ResetAction;

export const earningsWithdrawalReducer = (
  state: EarningsWithdrawalFlowState,
  action: EarningsWithdrawalAction,
): EarningsWithdrawalFlowState => {
  switch (action.type) {
    case EarningsWithdrawalActionType.destinationTypeChanged: {
      const destinationType = action.payload;
      if (destinationType === 'caliza_wallet') {
        return {
          ...state,
          destinationType,
          onlyMoveToCustody: true,
          targetCurrency: CurrencyCodeType.USD,
          paymentRail: PaymentRailType.CalizaWallet,
          recipientId: null,
        };
      }
      return {
        ...state,
        destinationType,
        onlyMoveToCustody: false,
        targetCurrency: CurrencyCodeType.UNSET,
        paymentRail: PaymentRailType.unset,
        recipientId: null,
      };
    }
    case EarningsWithdrawalActionType.recipientChanged: {
      return {
        ...state,
        recipientId: action.payload.recipientId,
        paymentRail: action.payload.paymentRail,
        targetCurrency: action.payload.currency,
      };
    }
    case EarningsWithdrawalActionType.amountChanged: {
      return {
        ...state,
        fromAmount: action.payload,
      };
    }
    case EarningsWithdrawalActionType.targetCurrencyChanged: {
      return {
        ...state,
        targetCurrency: action.payload,
      };
    }
    case EarningsWithdrawalActionType.reset: {
      return initialState;
    }
    default:
      return state;
  }
};

export const destinationTypeChanged = (payload: DestinationType): DestinationTypeChangedAction => ({
  type: EarningsWithdrawalActionType.destinationTypeChanged,
  payload,
});

export const recipientChanged = (
  payload: RecipientChangedAction['payload'],
): RecipientChangedAction => ({
  type: EarningsWithdrawalActionType.recipientChanged,
  payload,
});

export const amountChanged = (payload: number | null): AmountChangedAction => ({
  type: EarningsWithdrawalActionType.amountChanged,
  payload,
});

export const targetCurrencyChanged = (payload: CurrencyCodeType): TargetCurrencyChangedAction => ({
  type: EarningsWithdrawalActionType.targetCurrencyChanged,
  payload,
});
